import {
  type Control,
  Controller,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'

/**
 * DS `Select` (単一選択) を React Hook Form に繋ぐ共通部品。
 *
 * 見た目・modal 内の portal 対策・グループ描画・既存値フォールバックの実体は
 * [[DsSelect]] にある (本部品は `Controller` の配線と number 変換だけを足す)。
 * **RHF を使わない画面 (useState 制御) では [[DsSelect]] を直接使う**。
 *
 * ## なぜ native select ではなく DS Select か
 *
 * 単一 select の正は **DS の `Select`** (2026-08-05 デザイナー確定)。フィルタ側は
 * 先行して移行済みで、フォーム側 (新規追加・編集モーダル) が native select のまま
 * 残っていたため、同一画面内で見た目が割れていた。
 *
 * **[design 差異]** prototype (8080) はフィルタ・モーダルとも native select
 * (`.ecl-select`) を使う。本部品は prototype ではなく **DS 部品への統一**を選ぶ
 * デザイナー判断に基づく (フィルタ側で同じ判断を先に適用済み)。
 *
 * ## なぜ `register` ではなく `Controller` か
 *
 * DS `Select` は native `<select>` ではないため `register()` の ref/onChange を
 * そのまま受け取れない。`Controller` 経由で値を橋渡しする。call site 側の
 * boilerplate を増やさないよう、`Controller` は本部品の内側に閉じ込める。
 *
 * ## numeric
 *
 * proto enum のように **form 側が number を期待する** field では `numeric` を渡す。
 * 変換は native select 時代と同じ `enumSetValueAs` (`~/components/common/EnumSelect`)
 * に委ねる — 空文字 / null / NaN → `undefined` の扱いを 1 箇所に集約するため。
 *
 * ## 選択時の副作用 (`onValueChange`)
 *
 * 「事業者を変えたらライセンス選択肢を初期化する」 「別 field を `setValue` で同期する」
 * のように、 選択に副作用が伴う form が多い。 その口が `onValueChange`。
 *
 * ```tsx
 * <FormSelect
 *   control={control}
 *   name="businessCode"
 *   options={businessOptions}
 *   onValueChange={() => {
 *     setValue('licenseType', undefined)
 *   }}
 * />
 * ```
 *
 * - form への反映 (`field.onChange`) は**従来どおり本部品が内部で行い、 その後に**
 *   `onValueChange` を呼ぶ。 したがって callback 内の `getValues()` は新しい値を返す。
 * - 渡ってくるのは **option の生の value (string)**。 `numeric` を付けていても string の
 *   ままなので、 number が要るなら callback 側で `enumSetValueAs(value)` を通す
 *   (変換 helper は 1 つに揃える)。
 * - clear された時は `undefined`。
 *
 * ## 選択肢のグループ / 既存値のフォールバック
 *
 * `options` は `{ label, options }` を混ぜた入れ子 (= `<optgroup>` 相当) を受け付け、
 * `fallbackLabel` は「候補に無い既存値」の表示を担う。 いずれも実体と判断理由は
 * [[DsSelect]] の JSDoc にある。
 *
 * ## onBlur
 *
 * `onBlur` は `field.onBlur` に繋ぐ。現在の呼び出し元は validation mode が既定
 * (onSubmit) で touched を見ないが、後続バッチで `mode: 'onBlur'` の form を移行した
 * 時に silent に validation が走らなくなるのを防ぐ。
 */
type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  options: DsSelectOptions
  placeholder?: string
  'aria-label'?: string
  isDisabled?: boolean
  /** form へ number で返す (proto enum 等)。省略時は option の value (string) をそのまま返す */
  numeric?: boolean
  /** 選択確定後 (= form 反映後) に呼ばれる副作用フック。 clear 時は `undefined`。 */
  onValueChange?: (value: string | undefined) => void
  /** `value` が `options` に無い時の表示ラベル。 詳細は [[DsSelect]] の JSDoc。 */
  fallbackLabel?: (value: string) => string | undefined
  /** 欄が mount した時の値 (`Controller` の `defaultValue`)。 form の `defaultValues` に無い欄に使う。 */
  defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>
  /**
   * 内側 input の `id`。 外部の `<label htmlFor>` と関連付ける特殊レイアウト
   * (例: 特異日フォームの 2 カラム grid は label が sibling) でのみ渡す。
   * 通常は `FieldRoot` + `FieldLabel` の field context で関連付くので不要。
   */
  inputId?: string
  /**
   * 幅。 [[DsSelect]] へそのまま渡す。
   * modal の form 項目のように「ラベル行と同じ幅で横いっぱいに敷きたい」時に
   * `"100%"` を渡す (react-select の container は内容幅に縮むため、 明示しないと
   * 他の入力欄と横幅が揃わない)。
   */
  width?: string
  /** 幅の下限と flex での縮み。 [[DsSelect]] の同名 prop へそのまま渡す。 */
  minWidth?: string
  flexShrink?: number
}

export const FormSelect = <TFieldValues extends FieldValues>({
  control,
  name,
  options,
  placeholder,
  'aria-label': ariaLabel,
  isDisabled,
  numeric = false,
  onValueChange,
  fallbackLabel,
  defaultValue,
  inputId,
  width,
  minWidth,
  flexShrink,
}: Props<TFieldValues>): React.ReactElement => (
  <Controller
    control={control}
    defaultValue={defaultValue}
    name={name}
    render={({ field }) => (
      <DsSelect
        flexShrink={flexShrink}
        width={width}
        minWidth={minWidth}
        aria-label={ariaLabel}
        fallbackLabel={fallbackLabel}
        inputId={inputId}
        // React Hook Form の `shouldFocusError` (既定 true) は submit 失敗時に
        // 最初のエラー項目の `ref` を focus する。 `Controller` は ref を自動では
        // 繋がないので、 内側 input を拾って `field.ref` に渡す。 これが無いと
        // 長い form で「保存に失敗したがどこが未入力か分からない」状態になる
        // (native select 時代は register / ref 経由で効いていた)。
        inputRef={field.ref}
        isDisabled={isDisabled}
        onBlur={field.onBlur}
        onChange={(raw) => {
          field.onChange(numeric ? enumSetValueAs(raw) : raw)
          // 副作用は form 反映の**後**に呼ぶ (callback 内の getValues() が新値を返すため)。
          onValueChange?.(raw)
        }}
        options={options}
        placeholder={placeholder}
        value={field.value as string | number | null | undefined}
      />
    )}
  />
)
